import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { 
  Calendar,
  Mail,
  Phone,
  MessageSquare,
  Clock,
  CheckCircle,
  ArrowRight,
  Zap,
  Target,
  BarChart3,
  Users
} from "lucide-react";

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    website: "",
    monthlyRevenue: "",
    currentSpend: "",
    mainGoal: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast({
      title: "Audit Request Submitted!",
      description: "We'll analyze your business and get back to you within 24 hours with a custom growth plan.",
    });
    
    // Reset form
    setFormData({
      name: "",
      email: "",
      company: "",
      website: "",
      monthlyRevenue: "",
      currentSpend: "",
      mainGoal: "",
      message: ""
    });
    
    setIsSubmitting(false);
  };

  const auditIncludes = [
    "Current performance analysis",
    "Competitor benchmarking",
    "Growth opportunity assessment",
    "Custom strategy recommendations",
    "ROI projections & timeline",
    "30-minute strategy call"
  ];

  const contactMethods = [
    {
      icon: <Mail className="h-6 w-6" />,
      title: "Email Us",
      description: "hello@growthpro.agency",
      action: "Send Email"
    },
    {
      icon: <Phone className="h-6 w-6" />,
      title: "Call Us",
      description: "+1 (555) 123-4567",
      action: "Call Now"
    },
    {
      icon: <MessageSquare className="h-6 w-6" />,
      title: "WhatsApp",
      description: "Quick questions & support",
      action: "Message Us"
    },
    {
      icon: <Calendar className="h-6 w-6" />,
      title: "Book a Call",
      description: "Schedule a strategy session",
      action: "Book Now"
    }
  ];

  const faqs = [
    {
      question: "How long does the free audit take?",
      answer: "We typically deliver your comprehensive audit within 24-48 hours of receiving your information."
    },
    {
      question: "What's included in the free growth audit?",
      answer: "Complete performance analysis, competitor research, growth opportunities, custom strategy recommendations, and a 30-minute strategy call."
    },
    {
      question: "Do you work with businesses of all sizes?",
      answer: "We work with e-commerce brands, startups, and scaling companies typically spending $5K+ per month on marketing."
    },
    {
      question: "What platforms do you specialize in?",
      answer: "Meta Ads, Google Ads, TikTok Ads, Snapchat Ads, plus video production and AI-powered content creation."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 lg:pt-32 lg:pb-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <Badge variant="secondary" className="mb-6">
              <Zap className="h-3 w-3 mr-1" />
              Free Growth Audit
            </Badge>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Get Your <span className="text-gradient">Free Growth Audit</span>
            </h1>
            
            <p className="text-xl text-muted-foreground mb-8">
              Discover exactly how to 3x your ROAS with our comprehensive performance analysis. 
              No commitments, just actionable insights delivered in 24 hours.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-20 lg:pb-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Form */}
            <div className="lg:col-span-2">
              <Card className="card-elegant">
                <CardHeader>
                  <CardTitle className="text-2xl">Tell Us About Your Business</CardTitle>
                  <p className="text-muted-foreground">
                    The more details you provide, the more personalized your audit will be.
                  </p>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => handleInputChange("name", e.target.value)}
                          placeholder="John Smith"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange("email", e.target.value)}
                          placeholder="john@company.com"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="company">Company Name *</Label>
                        <Input
                          id="company"
                          value={formData.company}
                          onChange={(e) => handleInputChange("company", e.target.value)}
                          placeholder="Your Company"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="website">Website URL</Label>
                        <Input
                          id="website"
                          value={formData.website}
                          onChange={(e) => handleInputChange("website", e.target.value)}
                          placeholder="https://yourwebsite.com"
                        />
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="revenue">Monthly Revenue</Label>
                        <Select value={formData.monthlyRevenue} onValueChange={(value) => handleInputChange("monthlyRevenue", value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select revenue range" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="under-10k">Under $10K</SelectItem>
                            <SelectItem value="10k-50k">$10K - $50K</SelectItem>
                            <SelectItem value="50k-100k">$50K - $100K</SelectItem>
                            <SelectItem value="100k-500k">$100K - $500K</SelectItem>
                            <SelectItem value="500k-1m">$500K - $1M</SelectItem>
                            <SelectItem value="over-1m">Over $1M</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="spend">Current Ad Spend</Label>
                        <Select value={formData.currentSpend} onValueChange={(value) => handleInputChange("currentSpend", value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select spend range" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="under-1k">Under $1K</SelectItem>
                            <SelectItem value="1k-5k">$1K - $5K</SelectItem>
                            <SelectItem value="5k-10k">$5K - $10K</SelectItem>
                            <SelectItem value="10k-25k">$10K - $25K</SelectItem>
                            <SelectItem value="25k-50k">$25K - $50K</SelectItem>
                            <SelectItem value="over-50k">Over $50K</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="goal">Main Growth Goal</Label>
                      <Select value={formData.mainGoal} onValueChange={(value) => handleInputChange("mainGoal", value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="What's your primary goal?" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="increase-roas">Increase ROAS</SelectItem>
                          <SelectItem value="scale-revenue">Scale Revenue</SelectItem>
                          <SelectItem value="reduce-cpa">Reduce CPA</SelectItem>
                          <SelectItem value="expand-platforms">Expand to New Platforms</SelectItem>
                          <SelectItem value="improve-creative">Improve Creative Performance</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="message">Additional Details</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => handleInputChange("message", e.target.value)}
                        placeholder="Tell us about your current challenges, what you've tried, and what success looks like for your business..."
                        rows={4}
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full btn-glow text-lg py-6"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Submitting..." : "Get My Free Audit"}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
            
            {/* Sidebar */}
            <div className="space-y-8">
              {/* What's Included */}
              <Card className="card-elegant">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Target className="h-5 w-5 text-primary" />
                    <span>What's Included</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {auditIncludes.map((item, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
              
              {/* Contact Methods */}
              <Card className="card-elegant">
                <CardHeader>
                  <CardTitle>Other Ways to Reach Us</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {contactMethods.map((method, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 rounded-lg bg-muted/50 hover:bg-muted/70 transition-colors cursor-pointer">
                      <div className="text-primary">
                        {method.icon}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-sm">{method.title}</div>
                        <div className="text-xs text-muted-foreground">{method.description}</div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
              
              {/* Response Time */}
              <Card className="card-elegant bg-primary/5 border-primary/20">
                <CardContent className="p-6 text-center">
                  <Clock className="h-8 w-8 text-primary mx-auto mb-3" />
                  <div className="font-semibold mb-2">24-Hour Response</div>
                  <div className="text-sm text-muted-foreground">
                    We'll analyze your business and deliver your custom growth audit within 24 hours.
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 lg:py-32 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              Frequently Asked <span className="text-gradient">Questions</span>
            </h2>
          </div>
          
          <div className="max-w-3xl mx-auto space-y-6">
            {faqs.map((faq, index) => (
              <Card key={index} className="card-elegant">
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-3">{faq.question}</h3>
                  <p className="text-muted-foreground">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default Contact;